import { useState } from 'react';
import { motion } from 'framer-motion';
import { Save, Loader2, Eye, EyeOff, User, Lock, CheckCircle } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

export default function AdminAccount() {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [errors, setErrors] = useState({});

  const validatePassword = (password) => {
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password)
    };
    return checks;
  };

  const passwordChecks = validatePassword(passwordForm.newPassword);
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
    setPasswordSuccess(false);
  };

  const handleSubmitPassword = async (e) => {
    e.preventDefault();
    
    // Validazione
    const newErrors = {};
    
    if (!passwordForm.currentPassword) {
      newErrors.currentPassword = 'Inserisci la password attuale';
    }
    
    if (!passwordForm.newPassword) {
      newErrors.newPassword = 'Inserisci la nuova password';
    } else if (!isPasswordValid) {
      newErrors.newPassword = 'La password non soddisfa i requisiti minimi';
    }
    
    if (!passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Conferma la nuova password';
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Le password non corrispondono';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setSaving(true);
    try {
      await api.put('/auth/change-password', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      
      toast.success('Password aggiornata con successo');
      setPasswordSuccess(true);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Password change error:', error);
      const message = error.response?.data?.detail || 'Errore nel cambio password';
      toast.error(message);
      
      if (message.includes('attuale')) {
        setErrors({ currentPassword: message });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-account-page">
      <div className="mb-8">
        <h1 className="font-heading text-3xl mb-2">Account</h1>
        <p className="text-muted-foreground">Gestisci le impostazioni del tuo account</p>
      </div>

      <div className="grid gap-6 max-w-2xl">
        {/* Informazioni Account */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User size={20} />
              Informazioni Account
            </CardTitle>
            <CardDescription>
              I tuoi dati di accesso
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground text-xs">Nome</Label>
                <p className="font-medium">{user?.first_name} {user?.last_name}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Email</Label>
                <p className="font-medium">{user?.email}</p>
              </div>
            </div>
            <div>
              <Label className="text-muted-foreground text-xs">Ruolo</Label>
              <p className="font-medium capitalize">{user?.role}</p>
            </div>
          </CardContent>
        </Card>

        {/* Cambio Password */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock size={20} />
              Cambio Password
            </CardTitle>
            <CardDescription>
              Modifica la tua password di accesso
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmitPassword} className="space-y-4">
              {/* Password Attuale */}
              <div>
                <Label htmlFor="currentPassword" className="mb-2 block">
                  Password Attuale
                </Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    name="currentPassword"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordChange}
                    placeholder="Inserisci password attuale"
                    className={errors.currentPassword ? 'border-destructive' : ''}
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.currentPassword && (
                  <p className="text-destructive text-sm mt-1">{errors.currentPassword}</p>
                )}
              </div>

              {/* Nuova Password */}
              <div>
                <Label htmlFor="newPassword" className="mb-2 block">
                  Nuova Password
                </Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    name="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    placeholder="Inserisci nuova password"
                    className={errors.newPassword ? 'border-destructive' : ''}
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.newPassword && (
                  <p className="text-destructive text-sm mt-1">{errors.newPassword}</p>
                )}
                
                {/* Password Strength Indicator */}
                {passwordForm.newPassword && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs text-muted-foreground">Requisiti password:</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className={`flex items-center gap-1 ${passwordChecks.length ? 'text-green-600' : 'text-muted-foreground'}`}>
                        <CheckCircle size={14} />
                        Almeno 8 caratteri
                      </div>
                      <div className={`flex items-center gap-1 ${passwordChecks.uppercase ? 'text-green-600' : 'text-muted-foreground'}`}>
                        <CheckCircle size={14} />
                        Una maiuscola
                      </div>
                      <div className={`flex items-center gap-1 ${passwordChecks.lowercase ? 'text-green-600' : 'text-muted-foreground'}`}>
                        <CheckCircle size={14} />
                        Una minuscola
                      </div>
                      <div className={`flex items-center gap-1 ${passwordChecks.number ? 'text-green-600' : 'text-muted-foreground'}`}>
                        <CheckCircle size={14} />
                        Un numero
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Conferma Password */}
              <div>
                <Label htmlFor="confirmPassword" className="mb-2 block">
                  Conferma Nuova Password
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    placeholder="Ripeti nuova password"
                    className={errors.confirmPassword ? 'border-destructive' : ''}
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-destructive text-sm mt-1">{errors.confirmPassword}</p>
                )}
                {passwordForm.confirmPassword && passwordForm.newPassword === passwordForm.confirmPassword && (
                  <p className="text-green-600 text-sm mt-1 flex items-center gap-1">
                    <CheckCircle size={14} />
                    Le password corrispondono
                  </p>
                )}
              </div>

              {/* Success Message */}
              {passwordSuccess && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-sm p-4 flex items-center gap-2 text-green-700 dark:text-green-400"
                >
                  <CheckCircle size={18} />
                  Password aggiornata con successo!
                </motion.div>
              )}

              {/* Submit Button */}
              <Button 
                type="submit" 
                disabled={saving || !isPasswordValid || passwordForm.newPassword !== passwordForm.confirmPassword}
                className="w-full"
                data-testid="change-password-btn"
              >
                {saving ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Aggiornamento...
                  </>
                ) : (
                  <>
                    <Save size={16} className="mr-2" />
                    Aggiorna Password
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
